import { showToast } from 'Toast';
import { isNotAcceptableError, isNotLoggedInError, isNotPermittedError } from 'BackRoyalApi';
import { LOGGED_OUT_DESCRIPTION, messageFrom } from './serverErrors';

// Long enough to read a server message. A save failure leaves edits in the form, so the admin must
// have time to see why.
export const VALIDATION_TOAST_MS = 12000;

// How long the Save button reads 'Saved'.
export const SAVED_FLASH_MS = 1000;

function statusOf(error: unknown) {
    return (error as { status?: number | string })?.status;
}

/*
    RTK Query does not go through Angular's $http, so none of this reaches ApiErrorHandler. Every
    failure here would otherwise show the admin nothing at all.

    A stale version goes out through onStaleVersion, because a toast would read as a save that
    worked. `metadataSaved` says whether the SEO metadata was written first: the two requests are
    not one transaction.
*/
export function reportSaveFailure(
    error: unknown,
    onStaleVersion: (message: string) => void,
    { itemLabel, metadataSaved }: { itemLabel: string; metadataSaved: boolean },
) {
    const status = statusOf(error);
    const message = messageFrom(error, `The ${itemLabel} was not saved.`);
    const partialSave = metadataSaved ? ' The SEO metadata was saved.' : '';

    // Someone else saved this content item while it was open. The edits are still in the form.
    // 406 also carries plain validation failures, so the message is what separates the two.
    if (isNotAcceptableError(error) && /more recent version/i.test(message)) {
        onStaleVersion(message);
        return;
    }

    if (isNotLoggedInError(error) || isNotPermittedError(error)) {
        showToast({
            message: `The ${itemLabel} was not saved`,
            description: isNotLoggedInError(error)
                ? `${LOGGED_OUT_DESCRIPTION}${partialSave}`
                : `You are not allowed to publish or unpublish a ${itemLabel}.${partialSave}`,
            type: 'danger',
            timeout: VALIDATION_TOAST_MS,
        });
        return;
    }

    // A network failure or a timeout. The SEO metadata may already have been written, and a
    // timed-out save may have committed.
    if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR' || status === 0) {
        showToast({
            message: `The ${itemLabel} may not have been saved`,
            description: `The request did not finish. Your changes are still here. Reload the page before you edit further.${partialSave}`,
            type: 'danger',
            timeout: VALIDATION_TOAST_MS,
        });
        return;
    }

    showToast({
        message: `The ${itemLabel} was not saved`,
        description: `${message}${partialSave}`,
        type: 'danger',
        timeout: VALIDATION_TOAST_MS,
    });
}
